import type { StateSnapshot } from '@shared/types';
import { api } from './api';

const HTTP = import.meta.env.VITE_EVENTS_HTTP as string | undefined;
const REALTIME = import.meta.env.VITE_EVENTS_REALTIME as string | undefined;
const KEY = import.meta.env.VITE_EVENTS_API_KEY as string | undefined;
export type RtStatus = 'connecting' | 'live' | 'polling';

const b64url = (s: string) => btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

/**
 * GET /battles/{code}/state, with the snapshot version this page already holds as ?v= (nothing before the first one).
 * The server reads the battle again rather than answer with an older one, so a poll never steps a page backwards.
 */
export function statePath(code: string, version?: number): string {
  return Number.isInteger(version) && version! > 0 ? `/battles/${code}/state?v=${version}` : `/battles/${code}/state`;
}

/**
 * Subscribes to a battle's channel. Falls back to polling GET /state every second whenever the socket is not live.
 * Each poll says which version this page has: the highest seen here, or the page's own (`version`) when that is higher.
 */
export function connectBattle(code: string, h: { onSnapshot: (s: StateSnapshot) => void; onStatus: (s: RtStatus) => void; version?: () => number | undefined }) {
  let ws: WebSocket | undefined; let closed = false; let attempt = 0; let kaTimer: number | undefined; let pollTimer: number | undefined; let status: RtStatus = 'connecting';
  const channel = `default/battle/${code}`;
  let seen = 0;
  const deliver = (s: StateSnapshot) => {
    if (s && typeof s.code === 'string' && s.code.toUpperCase() === code.toUpperCase() && typeof s.version === 'number' && s.version > seen) seen = s.version;
    h.onSnapshot(s);
  };
  const known = () => { const v = h.version?.(); return typeof v === 'number' && v > seen ? v : seen; };

  const setStatus = (s: RtStatus) => { if (status !== s) { status = s; h.onStatus(s); } if (s === 'live') stopPolling(); else startPolling(); };
  const refresh = async () => { try { const r = await api<{ state: StateSnapshot }>(statePath(code, known())); deliver(r.state); } catch { /* keep going */ } };
  const startPolling = () => { if (pollTimer) return; pollTimer = window.setInterval(refresh, 1000); };
  const stopPolling = () => { if (pollTimer) { clearInterval(pollTimer); pollTimer = undefined; } };
  const armKa = () => { if (kaTimer) clearTimeout(kaTimer); kaTimer = window.setTimeout(() => { ws?.close(4000, 'no keep-alive'); }, 5 * 60_000 + 10_000); };

  const open = () => {
    if (closed) return; // a reconnect timer can fire after close(); never restart polling on an unmounted screen
    if (!HTTP || !REALTIME || !KEY) { setStatus('polling'); return; }
    setStatus(status === 'live' ? 'connecting' : status);
    const auth = { host: HTTP, 'x-api-key': KEY };
    try { ws = new WebSocket(`wss://${REALTIME}/event/realtime`, ['aws-appsync-event-ws', `header-${b64url(JSON.stringify(auth))}`]); }
    catch { setStatus('polling'); scheduleReconnect(); return; }
    ws.onopen = () => { ws?.send(JSON.stringify({ type: 'connection_init' })); };
    ws.onmessage = (ev) => {
      let m: any; try { m = JSON.parse(ev.data as string); } catch { return; }
      if (m.type === 'connection_ack') { armKa(); ws?.send(JSON.stringify({ type: 'subscribe', id: crypto.randomUUID(), channel, authorization: auth })); return; }
      if (m.type === 'ka') { armKa(); return; }
      if (m.type === 'subscribe_success') { attempt = 0; setStatus('live'); void refresh(); return; }
      if (m.type === 'data') {
        const events = Array.isArray(m.event) ? m.event : [m.event];
        for (const e of events) { try { deliver(typeof e === 'string' ? JSON.parse(e) : e); } catch { /* ignore malformed */ } }
        return;
      }
      if (m.type === 'subscribe_error' || m.type === 'connection_error') { ws?.close(4001, 'error'); }
    };
    ws.onerror = () => { /* onclose follows */ };
    ws.onclose = () => { if (kaTimer) clearTimeout(kaTimer); if (!closed) { setStatus('polling'); scheduleReconnect(); } };
  };
  const scheduleReconnect = () => { const delay = Math.min(15_000, 1000 * 2 ** attempt++) * (0.7 + Math.random() * 0.6); window.setTimeout(open, delay); };

  const onVisible = () => { if (document.visibilityState === 'visible') { void refresh(); if (!ws || ws.readyState > WebSocket.OPEN) open(); } };
  document.addEventListener('visibilitychange', onVisible);
  startPolling(); void refresh(); open();

  return { close() { closed = true; document.removeEventListener('visibilitychange', onVisible); stopPolling(); if (kaTimer) clearTimeout(kaTimer); ws?.close(1000, 'bye'); }, refresh };
}
